import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, ShieldAlert, XCircle } from "lucide-react";

export type StageMoveRequest = { dealId: string; dealName: string; stage: { id: string; name: string } } | null;

type MoveCheck = {
  kind: "forward" | "skip" | "backward" | "won" | "lost" | "reopen" | "same" | "ungated";
  allowed: boolean;
  needs_override: boolean;
  needs_reason: boolean;
  missing: string[];
  from_stage: string | null;
  to_stage: string;
};

/**
 * Every pipeline move goes through here (board drag, Move menu, deal page). An allowed move happens straight
 * away; otherwise it explains what is missing, asks for a reason to move back or reopen, and lets a
 * Sales Head / Super Admin override a gate with a reason. Won and Lost have their own dialogs.
 */
export function StageMoveDialog({
  request,
  onClose,
  onMoved,
}: {
  request: StageMoveRequest;
  onClose: () => void;
  onMoved: (stageName: string) => void;
}) {
  const { success, error } = useToast();
  const [check, setCheck] = useState<MoveCheck | null>(null);
  const [canOverride, setCanOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const doMove = async (override: boolean) => {
    if (!request) return;
    setBusy(true);
    try {
      await api.patch(`/api/Deal/MoveStage/${request.dealId}`, {
        stage_id: request.stage.id,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(override ? { override: true } : {}),
      });
      success("Deal moved", `"${request.dealName}" is now in ${request.stage.name}`);
      onMoved(request.stage.name);
      onClose();
    } catch (err: any) {
      error("Could not move the deal", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setCheck(null);
    setReason("");
    if (!request) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await api.get(`/api/Deal/Gates/${request.dealId}?target=${request.stage.id}`);
        if (cancelled) return;
        const move: MoveCheck = res?.move;
        setCanOverride(!!res?.can_override);
        if (move && move.allowed && !move.needs_reason) {
          // Nothing to ask: move straight away
          await doMove(false);
          return;
        }
        if (move?.kind === "same") {
          onClose();
          return;
        }
        setCheck(move);
      } catch (err: any) {
        if (!cancelled) {
          error("Could not check the move", err.message || "Please try again");
          onClose();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.dealId, request?.stage.id]);

  const open = !!request && !!check;
  const blocked = !!check && !check.allowed;
  const backward = check?.kind === "backward" || check?.kind === "reopen";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        {check && request && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {blocked ? (
                  <>
                    <AlertTriangle className="size-5 text-amber-500" /> Not ready for {request.stage.name} yet
                  </>
                ) : check.kind === "reopen" ? (
                  <>Reopen deal in {request.stage.name}</>
                ) : (
                  <>
                    <ArrowLeft className="size-5 text-muted-foreground" /> Move back to {request.stage.name}
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                <strong className="text-foreground">{request.dealName}</strong>
                {check.from_stage ? ` · now in ${check.from_stage}` : ""}
              </DialogDescription>
            </DialogHeader>

            {blocked && (
              <div className="space-y-2.5">
                <p className="text-sm font-medium">Finish these first:</p>
                <ul className="space-y-2">
                  {check.missing.map((m) => (
                    <li key={m} className="flex items-start gap-2 text-sm">
                      <XCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
                {!canOverride && (
                  <p className="text-xs sm:text-sm text-muted-foreground pt-1">
                    Complete them on the deal page. If there's a good reason to move anyway, ask your Sales Head.
                  </p>
                )}
              </div>
            )}

            {(backward || (blocked && canOverride)) && (
              <div className="space-y-2.5">
                {blocked && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs sm:text-sm text-amber-900">
                    <ShieldAlert className="size-4 shrink-0 mt-0.5" />
                    <span>As a manager you can override this. The reason and what was skipped are saved in the deal history and the audit log.</span>
                  </div>
                )}
                <Label htmlFor="move-reason">
                  {blocked ? "Why move anyway?" : check.kind === "reopen" ? "Why reopen it?" : "Why move it back?"}
                </Label>
                <Textarea
                  id="move-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder={blocked ? "e.g. Buyer placed the order by phone, PO to follow" : "e.g. Buyer asked to redo the sample"}
                  autoFocus
                />
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                {blocked && !canOverride ? "OK" : "Cancel"}
              </Button>
              {(backward || (blocked && canOverride)) && (
                <Button type="button" onClick={() => doMove(blocked)} disabled={busy || !reason.trim()}>
                  {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : blocked ? <ShieldAlert className="size-4 mr-1.5" /> : <CheckCircle2 className="size-4 mr-1.5" />}
                  {blocked ? "Override and move" : check.kind === "reopen" ? "Reopen" : "Move back"}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
